// "Mode details" card for the TLS mode selector.
//
// Mirrors the Cloudflare dashboard's mode card: a Browser -> Cloudflare ->
// Origin strip showing where TLS actually terminates, a list of the
// protections the current configuration turns on, and a "used for" line. The
// whole card is derived from the form value, so it updates as the operator
// changes mode, encryption mode, certificate source, origin lock and mTLS.
//
// Signals are split by what each option actually protects, so no glyph carries
// two meanings:
//   - the strip carries per-hop encryption (lock glyph + protocol + solid vs
//     dashed connector),
//   - the origin tile carries the origin lock (who may connect at all),
//   - hop 2 gains a reverse arrowhead under Authenticated Origin Pulls, the
//     one genuinely mutual thing here,
//   - everything else is spelled out in the notes list.
//
// Colour is never the only signal: each hop's protocol is written on it, the
// notes repeat it in prose, and every hop carries a screen-reader sentence.

import { Badge } from "@veil/ui/components/badge";
import { CloudflareIcon, LetsEncryptIcon } from "@veil/ui/components/brand";
import {
	Card,
	CardAction,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@veil/ui/components/card";
import BadgeCheck from "lucide-react/dist/esm/icons/badge-check";
import ChevronLeft from "lucide-react/dist/esm/icons/chevron-left";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import CornerDownRight from "lucide-react/dist/esm/icons/corner-down-right";
import FileKey from "lucide-react/dist/esm/icons/file-key";
import KeyRound from "lucide-react/dist/esm/icons/key-round";
import Lock from "lucide-react/dist/esm/icons/lock";
import LockOpen from "lucide-react/dist/esm/icons/lock-open";
import Monitor from "lucide-react/dist/esm/icons/monitor";
import Server from "lucide-react/dist/esm/icons/server";
import ShieldAlert from "lucide-react/dist/esm/icons/shield-alert";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { ComponentType } from "react";
import { Fragment } from "react";
import { EASE_OUT_EXPO } from "@/lib/motion";
import type { CfFullCertSource, CfMode, TlsMode } from "./tls-api";

/** Left-to-right cadence between strip elements (node, hop, node, …). */
const STEP = 0.06;

type IconType = ComponentType<{ className?: string }>;

interface FlowNode {
	key: string;
	label: string;
	icon: IconType;
	/** Corner marker on the tile — currently only the Cloudflare origin lock. */
	marker?: { icon: IconType; srLabel: string };
}

interface FlowHop {
	key: string;
	/** Protocol shown on the connector — the non-colour signal. */
	label: string;
	encrypted: boolean;
	/** Both ends authenticate each other (Authenticated Origin Pulls). */
	mutual: boolean;
	/** Spoken description of the hop for assistive tech. */
	srLabel: string;
}

interface FlowNote {
	key: string;
	icon: IconType;
	text: string;
}

interface Flow {
	/** Changes only when the participant chain changes (edge vs direct). */
	shape: string;
	/** Mode name for the header badge, matching Cloudflare's vocabulary. */
	badge: string;
	nodes: FlowNode[];
	hops: FlowHop[];
	notes: FlowNote[];
	usedFor: string;
}

const BROWSER: FlowNode = { key: "browser", label: "Browser", icon: Monitor };
const CLOUDFLARE: FlowNode = {
	key: "cloudflare",
	label: "Cloudflare",
	icon: CloudflareIcon,
};
const ORIGIN: FlowNode = { key: "origin", label: "This host", icon: Server };
const LOCKED_ORIGIN: FlowNode = {
	...ORIGIN,
	marker: {
		icon: ShieldCheck,
		srLabel: "Origin locked to Cloudflare IP ranges",
	},
};

const EDGE_HOP: FlowHop = {
	key: "edge",
	label: "HTTPS",
	encrypted: true,
	mutual: false,
	srLabel: "Browser to Cloudflare: encrypted over HTTPS",
};

const REDIRECT_NOTE: FlowNote = {
	key: "redirect",
	icon: CornerDownRight,
	text: "Port 80 stays open and redirects to HTTPS.",
};

const ORIGIN_LOCK_NOTE: FlowNote = {
	key: "origin-lock",
	icon: ShieldCheck,
	text: "Origin lock: direct connections from outside Cloudflare's IP ranges are refused.",
};

const MTLS_NOTE: FlowNote = {
	key: "mtls",
	icon: KeyRound,
	text: "Authenticated Origin Pulls: this host verifies the certificate Cloudflare presents.",
};

interface FlowInput {
	mode: TlsMode;
	cfMode: CfMode;
	fullCertSource: CfFullCertSource;
	originLock: boolean;
	mtlsEnabled: boolean;
}

function buildFlow({
	mode,
	cfMode,
	fullCertSource,
	originLock,
	mtlsEnabled,
}: FlowInput): Flow {
	if (mode === "cloudflare") {
		const full = cfMode === "full";
		const originCa = full && fullCertSource === "origin-ca";
		// mTLS and the certificate source only exist on the Full path; the API
		// clears both when Flexible is saved.
		const mutual = full && mtlsEnabled;
		const notes: FlowNote[] = [];

		if (full) {
			notes.push(
				originCa
					? {
							key: "cert-origin-ca",
							icon: BadgeCheck,
							text: "A 15-year Cloudflare Origin CA certificate is issued to this host. Set the zone to Full (strict) so Cloudflare validates it.",
						}
					: {
							key: "cert-self-signed",
							icon: ShieldAlert,
							text: "A self-signed certificate terminates the Cloudflare hop. Traffic is encrypted, but Cloudflare does not validate it.",
						},
			);
		} else {
			notes.push({
				key: "flexible-plaintext",
				icon: LockOpen,
				text: "The Cloudflare hop runs as plain HTTP on port 80 — readable by anything on the network path.",
			});
		}
		if (mutual) notes.push(MTLS_NOTE);
		if (originLock) notes.push(ORIGIN_LOCK_NOTE);

		return {
			shape: "edge",
			badge: full ? (originCa ? "Full (strict)" : "Full") : "Flexible",
			nodes: [BROWSER, CLOUDFLARE, originLock ? LOCKED_ORIGIN : ORIGIN],
			hops: [
				EDGE_HOP,
				full
					? {
							key: "origin",
							label: "HTTPS",
							encrypted: true,
							mutual,
							srLabel: `Cloudflare to this host: encrypted over HTTPS using ${
								originCa ? "a Cloudflare Origin CA" : "a self-signed"
							} certificate${
								mutual ? ", with the origin verifying Cloudflare in turn" : ""
							}`,
						}
					: {
							key: "origin",
							label: "HTTP",
							encrypted: false,
							mutual: false,
							srLabel: "Cloudflare to this host: not encrypted, plain HTTP",
						},
			],
			notes,
			usedFor: full
				? originCa
					? "Production traffic. Encrypted and validated on both hops."
					: "Encrypted end to end, without Cloudflare validating the origin certificate."
				: "Origins that cannot serve TLS yet. Not for sensitive traffic.",
		};
	}

	if (mode === "acme") {
		return {
			shape: "direct",
			badge: "Let's Encrypt",
			nodes: [BROWSER, ORIGIN],
			hops: [
				{
					key: "direct",
					label: "HTTPS",
					encrypted: true,
					mutual: false,
					srLabel:
						"Browser to this host: encrypted over HTTPS using a Let's Encrypt certificate",
				},
			],
			notes: [
				{
					key: "acme-cert",
					icon: LetsEncryptIcon,
					text: "A Let's Encrypt certificate is issued and auto-renewed on this host.",
				},
				REDIRECT_NOTE,
			],
			usedFor:
				"Public hostnames served straight from this host, with no proxy in front.",
		};
	}

	return {
		shape: "direct",
		badge: "Custom",
		nodes: [BROWSER, ORIGIN],
		hops: [
			{
				key: "direct",
				label: "HTTPS",
				encrypted: true,
				mutual: false,
				srLabel:
					"Browser to this host: encrypted over HTTPS using your certificate",
			},
		],
		notes: [
			{
				key: "byo-cert",
				icon: FileKey,
				text: "Your certificate and key are stored on this host and served directly.",
			},
			REDIRECT_NOTE,
		],
		usedFor: "Certificates you already manage, such as a corporate wildcard.",
	};
}

/**
 * Live "Mode details" card for the selected TLS mode. Driven purely by the
 * form value, so the wizard step and the settings card stay in sync for free.
 */
export function TlsFlowDiagram({
	mode,
	cfMode,
	fullCertSource,
	originLock,
	mtlsEnabled,
	className,
}: FlowInput & { className?: string }) {
	const reduce = !!useReducedMotion();
	const flow = buildFlow({
		mode,
		cfMode,
		fullCertSource,
		originLock,
		mtlsEnabled,
	});

	return (
		<Card size="sm" className={className}>
			<CardHeader>
				<CardTitle className="text-[0.68rem] font-medium tracking-[0.14em] text-muted-foreground uppercase">
					Mode details
				</CardTitle>
				<CardAction>
					<AnimatePresence mode="wait" initial={false}>
						<motion.span
							key={flow.badge}
							initial={reduce ? false : { opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={reduce ? undefined : { opacity: 0 }}
							transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
						>
							<Badge variant="secondary">{flow.badge}</Badge>
						</motion.span>
					</AnimatePresence>
				</CardAction>
			</CardHeader>

			<CardContent className="flex flex-col gap-3">
				<AnimatePresence mode="wait" initial={false}>
					<motion.div
						key={flow.shape}
						initial={reduce ? false : { opacity: 0, y: 8 }}
						animate={{ opacity: 1, y: 0 }}
						exit={reduce ? undefined : { opacity: 0, y: -8 }}
						transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
						className="@container/strip flex items-start"
					>
						{flow.nodes.map((node, i) => {
							const hop = i > 0 ? flow.hops[i - 1] : undefined;
							return (
								<Fragment key={node.key}>
									{hop && <Hop hop={hop} order={i * 2 - 1} reduce={reduce} />}
									<Node node={node} order={i * 2} reduce={reduce} />
								</Fragment>
							);
						})}
					</motion.div>
				</AnimatePresence>

				<ul className="flex flex-col text-xs leading-relaxed text-muted-foreground">
					<AnimatePresence initial={false}>
						{flow.notes.map((note) => (
							<motion.li
								key={note.key}
								initial={reduce ? false : { opacity: 0, height: 0 }}
								animate={{ opacity: 1, height: "auto" }}
								exit={reduce ? undefined : { opacity: 0, height: 0 }}
								transition={{ duration: 0.2, ease: EASE_OUT_EXPO }}
								className="overflow-hidden"
							>
								<span className="flex gap-2 pb-1.5">
									<note.icon className="mt-0.5 size-3.5 shrink-0" />
									<span>{note.text}</span>
								</span>
							</motion.li>
						))}
					</AnimatePresence>
				</ul>
			</CardContent>

			<CardFooter className="flex-col items-start gap-0.5">
				<span className="text-[0.68rem] font-medium tracking-[0.14em] text-muted-foreground uppercase">
					Used for
				</span>
				<AnimatePresence mode="wait" initial={false}>
					<motion.p
						key={flow.usedFor}
						initial={reduce ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={reduce ? undefined : { opacity: 0 }}
						transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
						className="text-xs leading-relaxed"
					>
						{flow.usedFor}
					</motion.p>
				</AnimatePresence>
			</CardFooter>
		</Card>
	);
}

function Node({
	node,
	order,
	reduce,
}: {
	node: FlowNode;
	order: number;
	reduce: boolean;
}) {
	const Icon = node.icon;
	const Marker = node.marker?.icon;
	return (
		<motion.div
			initial={reduce ? false : { opacity: 0, y: 4 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{
				duration: 0.25,
				delay: reduce ? 0 : order * STEP,
				ease: EASE_OUT_EXPO,
			}}
			className="flex w-9 shrink-0 flex-col items-center gap-1.5"
		>
			<div className="relative flex size-9 items-center justify-center rounded-lg bg-background ring-1 ring-foreground/10">
				<Icon className="size-4 text-muted-foreground" />
				<AnimatePresence initial={false}>
					{Marker && node.marker && (
						<motion.span
							key="marker"
							initial={reduce ? false : { opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={reduce ? undefined : { opacity: 0, scale: 0.8 }}
							transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
							className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-card ring-1 ring-foreground/10"
						>
							<Marker className="size-2.5 text-muted-foreground" />
							<span className="sr-only">{node.marker.srLabel}</span>
						</motion.span>
					)}
				</AnimatePresence>
			</div>
			{/* Labels sit below the connectors, so letting them spill past the
			    tile's width is safe and buys the hops room on narrow panels. */}
			<span className="text-center text-[0.65rem] leading-4 whitespace-nowrap text-muted-foreground">
				{node.label}
			</span>
		</motion.div>
	);
}

function Hop({
	hop,
	order,
	reduce,
}: {
	hop: FlowHop;
	order: number;
	reduce: boolean;
}) {
	const Glyph = hop.encrypted ? Lock : LockOpen;
	const base = reduce ? 0 : order * STEP;

	return (
		<div className="flex min-w-0 flex-1 flex-col items-center gap-1 px-1">
			<motion.div
				initial={reduce ? false : { opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{
					duration: 0.2,
					delay: base + (reduce ? 0 : 0.14),
					ease: EASE_OUT_EXPO,
				}}
				className="flex min-w-0"
			>
				<AnimatePresence mode="wait" initial={false}>
					<motion.span
						key={hop.label}
						initial={reduce ? false : { opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={reduce ? undefined : { opacity: 0 }}
						transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
					>
						<Badge
							variant={hop.encrypted ? "success" : "warning"}
							className="gap-1 px-1.5"
						>
							<Glyph aria-hidden="true" />
							{hop.label}
						</Badge>
					</motion.span>
				</AnimatePresence>
				<span className="sr-only">{hop.srLabel}</span>
			</motion.div>

			<motion.div
				initial={reduce ? false : { clipPath: "inset(0% 100% 0% 0%)" }}
				animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
				transition={{ duration: 0.25, delay: base, ease: EASE_OUT_EXPO }}
				className="flex w-full items-center text-muted-foreground/70"
				aria-hidden="true"
			>
				{/* Mutual auth reads as a connector with heads on both ends. The
				    heads are the first thing to go when the strip gets narrow —
				    they cost more room than the line can spare. */}
				<AnimatePresence initial={false}>
					{hop.mutual && (
						<motion.span
							key="reverse"
							initial={reduce ? false : { opacity: 0, width: 0 }}
							animate={{ opacity: 1, width: "auto" }}
							exit={reduce ? undefined : { opacity: 0, width: 0 }}
							transition={{ duration: 0.15, ease: EASE_OUT_EXPO }}
							className="flex overflow-hidden @max-[17rem]/strip:hidden"
						>
							<ChevronLeft className="size-2.5 shrink-0" />
						</motion.span>
					)}
				</AnimatePresence>
				<svg className="h-px min-w-0 flex-1" aria-hidden="true">
					<line
						x1="0"
						y1="0.5"
						x2="100%"
						y2="0.5"
						strokeWidth="1"
						strokeDasharray={hop.encrypted ? undefined : "3 3"}
						stroke="currentColor"
					/>
				</svg>
				<ChevronRight className="size-2.5 shrink-0 @max-[17rem]/strip:hidden" />
			</motion.div>
		</div>
	);
}
